<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import OT from "@opentok/client";

import type { iconName } from "@/Modules/App/Models/Styles/Icons";
import { DEVICE_TYPE } from "@/Modules/App/Models/Domain/AppState";
import {
  TRACK_KIND,
  VIDEO_CALL_NETWORK_QUALITY,
  VIDEO_CALL_STATUS,
  type Device,
  type streamsStatus,
} from "../../definitions";

import IconButton from "@/Components/Extend/TcIconButton.vue";
import ActionIconExtended from "./ActionIconExtended.vue";
import Settings from "./Settings.vue";

import useAppStore from "@/Modules/App/Store";
import useUserStore from "@/Modules/User/Store";
import useVideoCallStore from "@/Modules/VideoCall/Store";

import { isValidString } from "@/Utils/String";
import { log } from "@/Utils/Debug";
import { hasValue } from "@/Utils/Objects";
import { isApp } from "@/Utils/Ionic";

import { getDevices } from "../../utils";

export interface CallActionsProps {
  isBotEnabled: boolean;
  streamsStatus: streamsStatus;
  networkQuality: VIDEO_CALL_NETWORK_QUALITY;
  envDeviceId?: string;
  hideElements: boolean;
  hasBlur: boolean;
}

const appStore = useAppStore();
const userStore = useUserStore();
const videoCallStore = useVideoCallStore();

const { t } = useI18n();

const emit = defineEmits<{
  (event: "stopTrack", value: TRACK_KIND): void;
  (event: "startTrack", value: TRACK_KIND): void;
  (event: "endVideoCall", value: boolean): void;
  (event: "showChat"): void;
  (event: "startShareScreen"): void;
  (event: "stopShareScreen"): void;
  (event: "unsupportedShareScreen"): void;
  (event: "bot"): void;
  (event: "toggleBlur", value: boolean | undefined): void;
  (event: "clickOutside"): void;
  (event: "selectAudioDevice", value: OT.Device): void;
  (event: "selectVideoDevice", value: OT.Device): void;
}>();

const confirmStopSharing = ref<boolean>(false);
const isLoadingDevices = ref<boolean>(false);
const showSettings = ref<boolean>(false);
const devices = ref<Device[]>([]);
const openSelectors = reactive<{ video: boolean; audio: boolean; output: boolean }>({
  video: false,
  audio: false,
  output: false,
});
const selectedDevices = reactive<{
  video?: OT.Device;
  audio?: OT.Device;
  output?: OT.AudioOutputDevice;
}>({ video: undefined, audio: undefined, output: undefined });

const props = defineProps<CallActionsProps>();

const isCompactView = computed(() => appStore.isMobileViewport || isApp());

const videoDevices = computed(() => devices.value.filter((device) => device.kind === "videoInput"));
const audioDevices = computed(() => devices.value.filter((device) => device.kind === "audioInput"));
const outputDevices = computed(() => devices.value.filter((device) => device.kind === "audioOutput"));

const cameraSwapColor = computed(() =>
  appStore.device === DEVICE_TYPE.MOBILE ? "content-inverse" : "background-alt"
);

const camIcon = computed((): iconName => (props.streamsStatus.LOCAL.video ? "Cam" : "CamInactive"));

const audioIcon = computed((): iconName =>
  props.streamsStatus.LOCAL.audio ? "VoiceMessage" : "VoiceMessageActive"
);

const canSwapAvailable = computed(() => isValidString(props.envDeviceId));

const isSwap = computed(() => {
  if (userStore.getUserIsPatient) {
    return true;
  } else if (userStore.getUserIsTherapist && appStore.device === DEVICE_TYPE.MOBILE) {
    return true;
  }

  return false;
});

const cameraSwapIcon = computed(() => {
  if (isSwap.value) {
    return "CameraSwap";
  } else {
    return props.streamsStatus.SHARE_SCREEN.video ? "ShareScreenActive" : "ShareScreen";
  }
});

const isCameraSwapDisabled = computed(
  () =>
    !videoCallStore.isInCall ||
    (userStore.getUserIsPatient && appStore.device !== DEVICE_TYPE.MOBILE) ||
    (isSwap.value && !canSwapAvailable.value)
);

const handleClickShare = () => {
  if (userStore.getUserIsTherapist) {
    if (props.streamsStatus.SHARE_SCREEN.video !== true) {
      OT.checkScreenSharingCapability((resp) => {
        if (!resp.supported || resp.extensionRegistered === false || resp.extensionInstalled === false) {
          emit("unsupportedShareScreen");
        } else {
          emit("startShareScreen");
        }
      });
    } else {
      confirmStopSharing.value = true;
    }
  }
};

const stopSharing = () => {
  confirmStopSharing.value = false;

  emit("stopShareScreen");
};

const toggleTrack = (kind: TRACK_KIND) => {
  if (kind === TRACK_KIND.VIDEO) {
    if (props.streamsStatus.LOCAL.video) {
      emit("stopTrack", TRACK_KIND.VIDEO);
    } else {
      emit("startTrack", TRACK_KIND.VIDEO);
    }
  } else if (kind === TRACK_KIND.AUDIO) {
    // Por si añadimos mas tipos mañana no pongo un else
    if (props.streamsStatus.LOCAL.audio) {
      emit("stopTrack", TRACK_KIND.AUDIO);
    } else {
      emit("startTrack", TRACK_KIND.AUDIO);
    }
  }
};

const endVideoCall = () => {
  let isMissed = false;

  if (userStore.getUserIsTherapist && videoCallStore.status === VIDEO_CALL_STATUS.WAITING) {
    isMissed = true;
  }

  emit("endVideoCall", isMissed);
};

/* DEVICES */
const handleClickOutside = () => {
  if (!(openSelectors.video || openSelectors.audio || openSelectors.output)) {
    emit("clickOutside");
  }
};

const refreshDevices = async () => {
  const config = videoCallStore.getConfig();

  devices.value = await getDevices(t);

  selectedDevices.audio = undefined;
  selectedDevices.video = undefined;
  selectedDevices.output = undefined;

  devices.value.forEach((device) => {
    if (
      !hasValue(selectedDevices.video) &&
      device.kind === "videoInput" &&
      device.deviceId === config?.videoId
    ) {
      selectedDevices.video = device as OT.Device;
    } else if (
      !hasValue(selectedDevices.audio) &&
      device.kind === "audioInput" &&
      device.deviceId === config?.audioId
    ) {
      selectedDevices.audio = device as OT.Device;
    } else if (
      !hasValue(selectedDevices.output) &&
      device.kind === "audioOutput" &&
      device.deviceId === config?.outputId
    ) {
      selectedDevices.output = device;
    }
  });
};

const updateDevices = async () => {
  isLoadingDevices.value = true;

  devices.value = await getDevices(t);

  isLoadingDevices.value = false;
};

const handleOpenSelector = (selector: "video" | "audio" | "output") => {
  log("OPEN", selector);

  refreshDevices();

  openSelectors[selector] = true;
};

const handleCloseSelector = (selector: "video" | "audio" | "output") => {
  log("CLOSE", selector);

  openSelectors[selector] = true;
};

const handleSelectAudio = (device: OT.Device | OT.AudioOutputDevice) => {
  if (isValidString(device.deviceId)) {
    selectedDevices.audio = device as OT.Device;

    handleCloseSelector("audio");

    emit("selectAudioDevice", device as OT.Device);
  }
};

const handleSelectVideo = (device: OT.Device | OT.AudioOutputDevice) => {
  if (isValidString(device.deviceId)) {
    selectedDevices.video = device as OT.Device;

    handleCloseSelector("video");

    emit("selectVideoDevice", device as OT.Device);
  }
};

const handleSelectOutput = (device: OT.Device | OT.AudioOutputDevice) => {
  if (isValidString(device.deviceId)) {
    handleCloseSelector("output");

    selectedDevices.output = device;

    OT.setAudioOutputDevice(device.deviceId);
  }
};

/* DEVICES */

/* Settings */

const handleToggleSettings = () => {
  refreshDevices();

  showSettings.value = !showSettings.value;
};

/* Settings */

onMounted(() => {
  showSettings.value = false;

  refreshDevices();
});
</script>

<template>
  <div class="actions">
    <div
      class="actions__cover"
      :class="{ 'actions__cover--hide': props.hideElements }"
      @click="handleClickOutside"
    />

    <div class="actions__confirm_stop_share" @click="stopSharing" v-if="confirmStopSharing">
      <p>{{ t("chat.message.video_call.stop_sharing") }}</p>
    </div>

    <div v-if="showSettings" class="actions__settings">
      <Settings
        :isLoadingDevices="isLoadingDevices"
        :devices="devices"
        :blur="hasBlur"
        :video="selectedDevices.video as Device"
        :audio="selectedDevices.audio as Device"
        :output="selectedDevices.output as Device"
        :mirror="undefined"
        @updateBlur="(v) => emit('toggleBlur', v)"
        @updateVideo="(o) => handleSelectVideo(o as OT.Device)"
        @updateAudio="(o) => handleSelectAudio(o as OT.Device)"
        @updateOutput="(o) => handleSelectOutput(o as OT.AudioOutputDevice)"
        @close="handleToggleSettings"
        @updateDevices="updateDevices"
      />
    </div>

    <ActionIconExtended
      ref="selectAudioDeviceRef"
      id="call_audio"
      :canExtend="!isCompactView"
      :devices="audioDevices"
      :active="!!props.streamsStatus.LOCAL.audio"
      :icon="audioIcon"
      :selectedItem="selectedDevices.audio"
      @select="handleSelectAudio"
      @open="handleOpenSelector('audio')"
      @close="handleCloseSelector('audio')"
      @toggleTrack="toggleTrack(TRACK_KIND.AUDIO)"
    />

    <ActionIconExtended
      ref="selectVideoDeviceRef"
      id="call_camera"
      :canExtend="!isCompactView"
      :devices="videoDevices"
      :active="!!props.streamsStatus.LOCAL.video"
      :icon="camIcon"
      :selectedItem="selectedDevices.video"
      @select="handleSelectVideo"
      @open="handleOpenSelector('video')"
      @close="handleCloseSelector('video')"
      @toggleTrack="toggleTrack(TRACK_KIND.VIDEO)"
    />

    <ActionIconExtended
      v-if="outputDevices.length > 0 && !isCompactView"
      ref="selectAudioDeviceRef"
      id="call_output"
      icon="Headset"
      :devices="outputDevices"
      :active="true"
      :selectedItem="selectedDevices.output"
      @select="handleSelectOutput"
      @open="handleOpenSelector('output')"
      @close="handleCloseSelector('output')"
    />

    <IconButton
      v-if="!isSwap && !isApp()"
      id="call_swap_camera"
      class="actions__icon"
      :iconName="cameraSwapIcon"
      :color="cameraSwapColor"
      :class="{
        'actions__icon--disabled':
          isCameraSwapDisabled ||
          (props.networkQuality === VIDEO_CALL_NETWORK_QUALITY.LOWEST &&
            !props.streamsStatus.SHARE_SCREEN.video),
      }"
      @click="handleClickShare"
    />

    <IconButton
      v-if="OT.hasMediaProcessorSupport('video') && !isCompactView"
      :class="{ 'actions__icon--paused': !hasBlur }"
      id="call_blur"
      class="actions__icon"
      iconName="Blur"
      :color="hasBlur ? 'background-alt' : 'content-high'"
      @click="emit('toggleBlur', undefined)"
    />

    <IconButton v-if="false" iconName="Bot" size="lg" class="actions__icon__bot" @click="emit('bot')" />

    <IconButton
      id="call_chat"
      class="actions__icon"
      iconName="Chat"
      color="content-inverse"
      @click="emit('showChat')"
    />

    <IconButton
      v-if="isCompactView"
      id="call_settings"
      class="actions__icon actions__icon__config"
      iconName="Settings"
      color="content-inverse"
      @click="handleToggleSettings"
    />

    <IconButton
      id="call_end_call"
      class="actions__icon actions__icon__end_call"
      :isLoading="
        [VIDEO_CALL_STATUS.UNPUBLISHING, VIDEO_CALL_STATUS.CLOSING].includes(videoCallStore.status!)
      "
      iconName="HangCall"
      color="content-inverse"
      size="lg"
      @click="endVideoCall"
    />
  </div>
</template>

<style scoped lang="scss">
.actions {
  @include sp.padding(y, 2);

  display: flex;
  justify-content: center;
  align-items: center;

  &__cover {
    position: absolute;
    top: -100vh;
    background-color: transparent;
    width: 100%;
    height: 100vh;

    &--hide {
      top: -150vh;
      height: 300vh;
    }
  }

  &__confirm_stop_share {
    @include colors.bg(background, base);
    @include sp.padding(y, 4);
    @include radius(xl);
    @include text.modification(center);
    @include colors.color(content, high);
    @include text.general(low, l);

    position: absolute;
    top: -15px;
    left: 45%;
    transform: translate(-50%, -50%);
    min-width: 330px;
    cursor: pointer;
  }

  &__icon {
    @include sp.padding(false, 3);
    @include sp.margin(r, 4);
    @include colors.bg(background, alt-dark);

    display: flex;
    border-radius: 100%;
    overflow: hidden;

    &:last-child {
      @include sp.margin(r, 0, true);
    }

    &--disabled {
      cursor: initial;
      pointer-events: none;
      opacity: 60%;
    }

    &--paused {
      @include colors.bg(content, mid-solid, true);
    }

    &__end_call {
      @include colors.bg(semantic, error);
      @include sp.padding(x, 5);
      @include sp.padding(y, 2);

      border-radius: 100px;
    }

    &__bot {
      @include colors.bg(secondary, low-solid);
      @include sp.padding(false, 2);

      border-radius: 100%;
      position: absolute;
      right: 20px;
    }
  }

  &__settings {
    @include colors.bg(background, alt);
    @include radius(s);
    @include sp.padding(false, 4);

    position: absolute;
    top: -70vh;
    min-width: 460px;

    @media (max-width: $breakpoint-m) {
      width: calc(100% - 2 * $space-4);
      min-width: calc(100% - 2 * $space-4);
    }
  }
}
</style>
